import dayjs from "dayjs";
import { Smartphone, Watch } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  type WorkoutActivityHealthLinkMetadata,
  type WorkoutActivityHealthLinkSnapshot,
} from "@fitness-app/data-models/entities/ProgramActivity";
import { getHealthWorkoutActivityLabelKey } from "@fitness-app/utils/src/health/healthWorkoutActivityLabel";
import { cn } from "@fitness-app/utils/src/styles/cn";

import { PopoverSection } from "~/components/EventCalendar/EventPopover";

const PROVIDER_LABELS: Record<string, string> = {
  "apple-health": "Apple Health",
  "health-connect": "Health Connect",
};

const formatNumber = (value: number) => Intl.NumberFormat("pl-PL", { maximumFractionDigits: 1 }).format(value);

// Snapshot leży w bazie jako `unknown`, a starsze wiersze nie mają wszystkich pól.
const readSnapshot = (snapshot: unknown): Partial<WorkoutActivityHealthLinkSnapshot> => {
  if (!snapshot || typeof snapshot !== "object") {
    return {};
  }
  const value = snapshot as Partial<WorkoutActivityHealthLinkSnapshot>;
  return {
    title: typeof value.title === "string" ? value.title : null,
    activityType: typeof value.activityType === "string" ? value.activityType : null,
    startDate: typeof value.startDate === "string" ? value.startDate : undefined,
    endDate: typeof value.endDate === "string" ? value.endDate : undefined,
    durationSeconds: typeof value.durationSeconds === "number" ? value.durationSeconds : undefined,
    totalDistanceMeters: typeof value.totalDistanceMeters === "number" ? value.totalDistanceMeters : null,
    sourceName: typeof value.sourceName === "string" ? value.sourceName : null,
    heartRateSamples: Array.isArray(value.heartRateSamples)
      ? value.heartRateSamples.filter(
          (sample) => typeof sample?.bpm === "number" && typeof sample.startDate === "string",
        )
      : [],
  };
};

const formatDuration = (seconds: number) => {
  const minutes = Math.max(1, Math.round(seconds / 60));
  return minutes >= 60 ? `${Math.floor(minutes / 60)} h ${minutes % 60} min` : `${minutes} min`;
};

const StatTile = ({ label, value, unit }: { label: string; value: string; unit?: string }) => (
  <div className="flex min-w-0 flex-col gap-0.5 rounded-lg bg-gray-50 px-2.5 py-2">
    <span className="truncate text-[11px] text-gray-500">{label}</span>
    <span className="truncate text-sm font-semibold tabular-nums text-gray-900">
      {value}
      {unit && <span className="text-xs font-medium text-gray-500"> {unit}</span>}
    </span>
  </div>
);

const CHART_WIDTH = 288;
const CHART_HEIGHT = 56;
const MAX_CHART_POINTS = 120;

// Do 1000 próbek z zegarka: uśredniamy do ~120 punktów, więcej i tak nie zmieści się w 288 px.
const HeartRateChart = ({
  samples,
  wide,
}: {
  samples: WorkoutActivityHealthLinkSnapshot["heartRateSamples"];
  wide: boolean;
}) => {
  const sorted = [...samples].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const bucketSize = Math.ceil(sorted.length / MAX_CHART_POINTS);
  const points: number[] = [];
  for (let index = 0; index < sorted.length; index += bucketSize) {
    const bucket = sorted.slice(index, index + bucketSize);
    points.push(bucket.reduce((sum, sample) => sum + sample.bpm, 0) / bucket.length);
  }

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = Math.max(1, max - min);
  const coordinates = points.map((bpm, index) => {
    const x = points.length === 1 ? 0 : (index / (points.length - 1)) * CHART_WIDTH;
    const y = CHART_HEIGHT - 4 - ((bpm - min) / range) * (CHART_HEIGHT - 8);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const line = coordinates.join(" ");

  return (
    <div className="flex flex-col gap-1">
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className={cn("w-full overflow-visible", wide ? "h-40" : "h-14")}
        preserveAspectRatio="none"
        role="img"
        aria-label="Tętno w trakcie treningu"
      >
        <polygon points={`0,${CHART_HEIGHT} ${line} ${CHART_WIDTH},${CHART_HEIGHT}`} className="fill-rose-500/10" />
        <polyline
          points={line}
          fill="none"
          className="stroke-rose-500"
          strokeWidth={1.5}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="flex justify-between text-[11px] tabular-nums text-gray-400">
        <span>{dayjs(sorted[0]!.startDate).format("HH:mm")}</span>
        <span>
          {Math.round(min)} - {Math.round(max)} BPM
        </span>
        <span>{dayjs(sorted[sorted.length - 1]!.startDate).format("HH:mm")}</span>
      </div>
    </div>
  );
};

interface LinkedHealthWorkoutProps {
  linkedWorkout: WorkoutActivityHealthLinkMetadata;
  /** Zakładka w modalu treningu: kafelki w jednym rzędzie i wyższy wykres. */
  wide?: boolean;
}

export const LinkedHealthWorkout = ({ linkedWorkout, wide = false }: LinkedHealthWorkoutProps) => {
  const { t } = useTranslation();
  const snapshot = readSnapshot(linkedWorkout.snapshot);
  const isWatch = linkedWorkout.source === "watch";
  const Icon = isWatch ? Watch : Smartphone;

  const labelKey = getHealthWorkoutActivityLabelKey(linkedWorkout.provider, snapshot.activityType);
  const title = snapshot.title && snapshot.title !== "SylwetkaPro" ? snapshot.title : labelKey ? t(labelKey) : null;
  // „SourceProxy" to techniczna nazwa źródła z HealthKit, nic nie mówi trenerowi.
  const sourceName = snapshot.sourceName && snapshot.sourceName !== "SourceProxy" ? snapshot.sourceName : null;
  const source = sourceName || (isWatch ? "Apple Watch" : PROVIDER_LABELS[linkedWorkout.provider]);
  const timeRange =
    snapshot.startDate && snapshot.endDate
      ? `${dayjs(snapshot.startDate).format("HH:mm")} - ${dayjs(snapshot.endDate).format("HH:mm")}`
      : null;
  const heartRateRange =
    typeof linkedWorkout.heartRateMinBpm === "number" && typeof linkedWorkout.heartRateMaxBpm === "number"
      ? `${linkedWorkout.heartRateMinBpm} - ${linkedWorkout.heartRateMaxBpm}`
      : null;

  return (
    <PopoverSection label={isWatch ? "Trening z zegarka" : "Powiązany trening"}>
      <div className="flex items-center gap-2.5">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-500">
          <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold text-gray-900">{title ?? "Trening"}</div>
          <div className="truncate text-xs text-gray-500">{[source, timeRange].filter(Boolean).join(" · ")}</div>
        </div>
      </div>
      <div className={cn("grid gap-1.5", wide ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2")}>
        {typeof snapshot.durationSeconds === "number" && snapshot.durationSeconds > 0 && (
          <StatTile label="Czas" value={formatDuration(snapshot.durationSeconds)} />
        )}
        {typeof linkedWorkout.energyBurnedKcal === "number" && (
          <StatTile label="Kalorie" value={formatNumber(linkedWorkout.energyBurnedKcal)} unit="kcal" />
        )}
        {typeof linkedWorkout.heartRateAverageBpm === "number" && (
          <StatTile label="Śr. tętno" value={String(linkedWorkout.heartRateAverageBpm)} unit="BPM" />
        )}
        {heartRateRange && <StatTile label="Zakres tętna" value={heartRateRange} unit="BPM" />}
        {typeof snapshot.totalDistanceMeters === "number" && snapshot.totalDistanceMeters > 0 && (
          <StatTile label="Dystans" value={formatNumber(snapshot.totalDistanceMeters / 1000)} unit="km" />
        )}
      </div>
      {snapshot.heartRateSamples && snapshot.heartRateSamples.length > 1 && (
        <HeartRateChart samples={snapshot.heartRateSamples} wide={wide} />
      )}
      <span className="text-[11px] text-gray-400">
        Powiązano {dayjs(linkedWorkout.linkedAt).format("DD.MM.YYYY, HH:mm")}
      </span>
    </PopoverSection>
  );
};
